import { useCallback, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Spinner } from '../../../components/Spinner';
import Button from 'react-bootstrap/Button';
import { useAuth } from '../../../hooks/useAuth';
import { useEnv } from '../../../context/env-context';
import { makeRequest } from '../../../utils/makeRequest';
import { toast } from 'react-toastify';
import DashboardFunnel from './engagement/DashboardFunnel';
import DashboardConsumers from './engagement/DashboardConsumers';
import DashboardRewards from './engagement/DashboardRewards';

function DashboardEngagement({ aggregators, getAggregators }) {
  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [defaultDashboard, setDefaultDashboard] = useState(false);

  useEffect(() => {
    setDefaultDashboard(localStorage.getItem('dashboard') === 'engagement');
  }, []);

  const [spinner, setSpinner] = useState(false);

  const [aggregatorId, setAggregatorId] = useState(null);
  const [rewardsCategory, setRewardsCategory] = useState('');

  const [countsByAggregator, setCountsByAggregator] = useState({
    consumersByStatus: {},
    billableConsumers: null,
    allSubscribersToDate: null,
  });
  const [rewardsDashboard, setRewardsDashboard] = useState(null);
  const [funnelReport, setFunnelReport] = useState(null);

  const engagementGetRequest = useCallback(
    async ({ url, payload = {}, callback, customErrorMessage }) => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return null;
      }

      const config = { token, url, method: 'GET', params: { aggregatorId, ...payload } };

      try {
        const response = await makeRequest(config);

        if (callback) {
          callback(response);
        }

        return response;
      } catch (error) {
        toast.error(`${customErrorMessage} ${error.message}`);

        return null;
      }
    },
    [aggregatorId, getAccessTokenSilently],
  );

  const getRewardsDashboard = useCallback(
    () =>
      engagementGetRequest({
        url: `${apiOriginConsumer}/rewards/dashboard`,
        payload: { category: rewardsCategory },
        callback: res => setRewardsDashboard(res),
        customErrorMessage: 'Failed getting rewards:',
      }),
    [apiOriginConsumer, engagementGetRequest, rewardsCategory],
  );

  const getAllData = useCallback(async () => {
    setSpinner(true);
    await Promise.all([
      getAggregators(),
      engagementGetRequest({
        url: `${apiOriginConsumer}/get_counts_by_aggregator`,
        callback: res =>
          setCountsByAggregator({
            consumersByStatus: res.byStatus,
            billableConsumers: res.billableConsumers,
            allSubscribersToDate: res.allSubscribersToDate,
          }),
        customErrorMessage: 'Failed getting consumers:',
      }),
      getRewardsDashboard(),
      engagementGetRequest({
        url: `${apiOriginConsumer}/get_funnel_report`,
        callback: res => setFunnelReport(res),
        customErrorMessage: 'Failed getting funnel report:',
      }),
    ]);
    setSpinner(false);
  }, [getAggregators, engagementGetRequest, apiOriginConsumer, getRewardsDashboard]);

  useEffect(() => {
    getAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregatorId]);

  return (
    <Spinner visible={spinner}>
      <Row className="align-items-start">
        <Col md={12} className="mb-3 d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <Form.Select
              className="bo-filter w-100"
              value={aggregatorId ?? ''}
              onChange={e => setAggregatorId(e.target.value)}
            >
              <option value="">All Standard Aggregators</option>
              {aggregators.map(agg => (
                <option key={agg.id} value={agg.id}>
                  {agg.name}
                </option>
              ))}
            </Form.Select>
            <Button className="bo-button-inline-link" onClick={getAllData}>
              Refresh
            </Button>
          </div>
          <div className="d-flex">
            <Form.Check
              type="checkbox"
              checked={defaultDashboard}
              onChange={() => {
                setDefaultDashboard(prev => !prev);
                if (localStorage.getItem('dashboard') === 'engagement') {
                  localStorage.removeItem('dashboard');
                } else {
                  localStorage.setItem('dashboard', 'engagement');
                }
              }}
            />
            <Form.Label className="p-0 m-0">Make this my default dashboard</Form.Label>
          </div>
        </Col>
      </Row>
      <Row className="align-items-start">
        <Col md={6}>
          <DashboardConsumers
            consumersByStatus={countsByAggregator?.consumersByStatus}
            billableConsumers={countsByAggregator?.billableConsumers}
            allSubscribersToDate={countsByAggregator?.allSubscribersToDate}
          />
          <DashboardFunnel funnelReport={funnelReport} />
        </Col>
        <Col md={6}>
          <DashboardRewards
            rewardsDashboard={rewardsDashboard}
            getRewardsDashboard={getRewardsDashboard}
            rewardsCategory={rewardsCategory}
            setRewardsCategory={setRewardsCategory}
          />
        </Col>
      </Row>
    </Spinner>
  );
}

export default DashboardEngagement;
